import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import Stripe from "stripe";

import { Promo, PromoTypeConfig } from "../../Common/models/promos";
import { WLSubscriptionsTiers } from "../../Common/models/subscriptions";
import { formatDollarPriceDisplay, getErrorMsg } from "../../Common/utils/common";
import { getStripePriceValueWithPromo } from "../../Common/utils/stripePromoPrice";
import { bugNotif } from "../api/bugsnag";
import { validatePromoUnauthenticated } from "../api/promo";

interface iPromoValidParams extends PromoTypeConfig {
  code: string;
  userId?: string;
}

export const isPromoValid = async (data: iPromoValidParams) => {
  try {
    const result = await validatePromoUnauthenticated(data);
    return result.data.result;
  } catch (error) {
    const { rawErrorMsg: errorMsg } = getErrorMsg(error);
    bugNotif("isPromoValid", errorMsg);
    return null;
  }
};

export const getPromoLabel = (
  promo: Promo,
  isOneOff = false,
  existingMember = false
) => {
  const { entryPoints, rewardPoints, isMultiplier } = promo;

  let label = "Get";
  const hasEntryAndPoints = entryPoints > 0 && rewardPoints > 0;
  if (isMultiplier) {
    if (entryPoints > 0) {
      label = `${label} ${entryPoints + 1}X entries`;
    }
    if (rewardPoints > 0 && !isOneOff) {
      label = `${label}${hasEntryAndPoints ? ", and " : " "}${
        rewardPoints + 1
      }X points`;
    }
  } else {
    if (entryPoints > 0) {
      label = `${label} ${entryPoints} extra entries`;
    }
    if (rewardPoints > 0 && !isOneOff) {
      label = `${label}${
        hasEntryAndPoints ? ", and " : " "
      }${rewardPoints} extra points`;
    }
  }

  return `${label}${
    !isOneOff && !existingMember
      ? promo.bonusMonths !== undefined && promo.bonusMonths > 1
        ? ` for ${promo.bonusMonths} months`
        : " this month"
      : ""
  }`;
};

// used in v2 web sign up
export const getCouponDiscountAmount = (
  coupon: Stripe.Coupon,
  oneOff = false,
  amount: number
) => {
  const { amount_off, percent_off, duration } = coupon;

  let discountAmount = !isNull(amount_off)
    ? amount_off / 100
    : !isNull(percent_off)
      ? amount * parseFloat(`0.${percent_off}`)
      : 0;

  discountAmount =
    parseInt(discountAmount.toFixed()) < parseFloat(discountAmount.toFixed(1))
      ? parseFloat(discountAmount.toFixed(1))
      : parseInt(discountAmount.toFixed());

  if (oneOff || duration === "forever" || duration === "repeating") {
    return discountAmount;
  }

  return amount - discountAmount;
};

export const getCouponDiscountDescription = (
  coupon: Stripe.Coupon,
  oneOff = false
) => {
  const { duration } = coupon;
  let discountDescription = "";
  if (oneOff || duration === "forever" || duration === "repeating") {
    discountDescription = "OFF";
  } else {
    discountDescription = "Discount for the first month";
  }
  return discountDescription;
};

/** Stripe coupon discount in dollars, formatted for UI (includes `$`). */
export const getStripePromoAmount = (
  coupon: Stripe.Coupon,
  amount: number
): string => {
  const { amount_off, percent_off } = coupon;

  const raw = !isNull(amount_off)
    ? amount_off / 100
    : !isNull(percent_off)
      ? amount * parseFloat(`0.${percent_off}`)
      : 0;

  return formatDollarPriceDisplay(Math.round(raw * 100) / 100);
};

// TODO use getCouponDiscountAmount above
// to remove redundant logic
export const getStripePromoLabel = (
  coupon: Stripe.Coupon,
  oneOff = false,
  amount: number
) => {
  const { duration } = coupon;
  const discountLabel = getStripePromoAmount(coupon, amount);

  if (oneOff || duration === "forever" || duration === "repeating") {
    return `Save ${discountLabel}`;
  }

  return `Save ${discountLabel} for the first month`;
};

export const getStripeAndWLPromoLabel = (
  stripeCoupon: Stripe.Coupon | null,
  wlPromo: Promo | null
) => {
  if (isNull(stripeCoupon) && isNull(wlPromo)) {
    return "";
  }
  //If both once
  const stripeLabel = !isNull(stripeCoupon) ? "Discount" : "";
  const promoLabel = !isNull(wlPromo) ? "Promo" : "";
  const stripePromoLabel = compact([stripeLabel, promoLabel]).join(" and ");

  return !isEmpty(stripePromoLabel)
    ? `${stripePromoLabel} for the first month`
    : "";
};

export const getWLPromoDescription = (
  wlPromo: Promo,
  subscription: WLSubscriptionsTiers,
  isPowerUp = false,
  entriesOnly = false
) => {
  const entries = wlPromo.isMultiplier
    ? wlPromo.entryPoints * subscription.entriesPerMonth
    : wlPromo.entryPoints;

  const entriesLabel =
    entries > 0
      ? `${entries} bonus ${isPowerUp ? "power up " : ""}entries`
      : "";

  const rewards = wlPromo.isMultiplier
    ? wlPromo.rewardPoints * subscription.rewardPointsPerMonth
    : wlPromo.rewardPoints;

  const rewardsLabel =
    entriesOnly || !(rewards > 0) ? "" : `${rewards} bonus points`;

  return `get ${compact([entriesLabel, rewardsLabel]).join(" and ")}`;
};

// export const getWLPromoDescriptionNative = (
//   wlPromo: Promo,
//   subscription: WLSubscriptionsTiers
// ) => {
//   const entries = wlPromo.isMultiplier
//     ? wlPromo.entryPoints * subscription.entriesPerMonth
//     : wlPromo.entryPoints;

//   const entriesLabel = entries > 0 ? `${entries} bonus entries` : "";

//   const rewards = wlPromo.isMultiplier
//     ? wlPromo.rewardPoints * subscription.rewardPointsPerMonth
//     : wlPromo.rewardPoints;

//   const rewardsLabel = rewards > 0 ? `${rewards} bonus points` : "";

//   return `+ ${compact([entriesLabel, rewardsLabel]).join(" and ")} this month`;
// };

export const getWLPromoEntries = (
  wlPromo: Promo,
  subscription: WLSubscriptionsTiers
) => {
  const entries = wlPromo.isMultiplier
    ? wlPromo.entryPoints * subscription.entriesPerMonth
    : wlPromo.entryPoints;

  return entries;
};

export const getWLPromoPoints = (
  wlPromo: Promo,
  subscription: WLSubscriptionsTiers
) => {
  const points = wlPromo.isMultiplier
    ? wlPromo.rewardPoints * subscription.rewardPointsPerMonth
    : wlPromo.rewardPoints;

  return points;
};

export const getPointsWithPromo = (points: number, promo: Promo) => {
  let pointsToReturn = points;

  const { rewardPoints, isMultiplier } = promo;

  if (isMultiplier) {
    pointsToReturn += pointsToReturn * rewardPoints;
  } else {
    pointsToReturn += rewardPoints;
  }

  return pointsToReturn;
};

export { getStripePriceValueWithPromo };

export const getStripePriceWithPromo = (
  price: number,
  coupon: Stripe.Coupon
) => formatDollarPriceDisplay(getStripePriceValueWithPromo(price, coupon));

export const getEntriesWithPromo = (entries: number, promo: Promo) => {
  let entriesToReturn = entries;

  const { entryPoints, isMultiplier } = promo;

  if (isMultiplier) {
    entriesToReturn += entriesToReturn * entryPoints;
  } else {
    entriesToReturn += entryPoints;
  }

  return entriesToReturn;
};

export const getExtraEntriesWithPromo = (entries: number, promo: Promo) => {
  let entriesToReturn = entries;

  const { entryPoints, isMultiplier } = promo;

  if (isMultiplier) {
    entriesToReturn = entriesToReturn * entryPoints;
  } else {
    entriesToReturn = entryPoints;
  }

  return entriesToReturn;
};

export const getPriceWithStripeCredits = (
  price: number,
  discount: number,
  isPercentOff: boolean
) => {
  let total = 0;
  if (!isPercentOff) {
    total = price - discount;
  } else {
    total = price * ((100 - discount) / 100);
  }
  return formatDollarPriceDisplay(total);
};

export const getPriceWithStripeCreditsValue = (
  price: number,
  discount: number,
  isPercentOff: boolean
) => {
  let total = 0;
  if (!isPercentOff) {
    total = price - discount;
  } else {
    total = price * ((100 - discount) / 100);
  }
  return total;
};
